import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { bankingAPI, reconciliationAPI } from '../../../../api';
import Layout from '../../../../components/Layout';
import { Button } from '../../../../components/ui/Button';
import { Input } from '../../../../components/ui/Input';
import { Select } from '../../../../components/ui/Select';
import { Card, CardContent } from '../../../../components/ui/Card';
import { Badge } from '../../../../components/ui/Badge';
import { FormLabel } from '../../../../components/ui/FormLabel';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../../../components/ui/Table';
import {
  Upload,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  AlertTriangle,
  FileSpreadsheet,
  Save,
} from 'lucide-react';

/**
 * Phase 1 reconciliation import wizard.
 *
 * URL: /tenant/banking/reconciliation/accounts/:accountId/import
 *      (or /tenant/banking/reconciliation/import?account_id=...)
 *
 * Four steps (per the plan):
 *   1. Upload          — pick a CSV file; server decodes and suggests mapping.
 *   2. Map columns     — preview first 5 rows, confirm/override column mapping,
 *                        date format and sign convention. Optionally save
 *                        the whole lot as a named preset for next time.
 *   3. Validate        — full preview against the database:
 *                        new vs duplicate-in-file vs duplicate-existing vs error,
 *                        plus file-hash duplicate warning and date-range summary.
 *   4. Commit          — writes statement_lines inside a single DB transaction.
 *
 * Nothing is written to the database before step 4. The user can step back
 * and tweak the mapping at any time.
 */

const STEPS = [
  { id: 1, label: 'Upload' },
  { id: 2, label: 'Map columns' },
  { id: 3, label: 'Validate' },
  { id: 4, label: 'Commit' },
];

const MAPPING_FIELDS = [
  { key: 'date', label: 'Transaction date', required: true },
  { key: 'value_date', label: 'Value date (optional)' },
  { key: 'description', label: 'Description' },
  { key: 'reference', label: 'Reference' },
  { key: 'counterparty', label: 'Counterparty / payee' },
  { key: 'amount', label: 'Amount (signed)' },
  { key: 'debit', label: 'Debit / money out' },
  { key: 'credit', label: 'Credit / money in' },
  { key: 'running_balance', label: 'Running balance' },
];

const DATE_FORMATS = [
  'DD/MM/YYYY',
  'DD-MM-YYYY',
  'MM/DD/YYYY',
  'YYYY-MM-DD',
  'DD MMM YYYY',
];

const SIGN_CONVENTIONS = [
  { value: 'separate_debit_credit', label: 'Separate debit & credit columns' },
  { value: 'signed_amount', label: 'Single signed amount column' },
  { value: 'inverted_signed', label: 'Single signed amount (inverted sign)' },
];

const ENCODINGS = [
  { value: 'utf-8', label: 'UTF-8 (default)' },
  { value: 'utf-16le', label: 'UTF-16 LE' },
  { value: 'utf-16be', label: 'UTF-16 BE' },
  { value: 'windows-1252', label: 'Windows-1252 / Latin-1' },
];

const DELIMITERS = [
  { value: '', label: 'Auto-detect' },
  { value: ',', label: 'Comma  ,' },
  { value: ';', label: 'Semicolon  ;' },
  { value: '\t', label: 'Tab' },
  { value: '|', label: 'Pipe  |' },
];

function formatMoney(n) {
  if (n == null || n === '') return '';
  const num = Number(n);
  if (!Number.isFinite(num)) return String(n);
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
  }).format(num);
}

function statusBadge(status) {
  switch (status) {
    case 'new':
      return <Badge className="bg-green-600 text-white">New</Badge>;
    case 'duplicate_in_file':
      return <Badge className="bg-amber-500 text-white">Duplicate in file</Badge>;
    case 'duplicate_existing':
      return <Badge className="bg-amber-600 text-white">Already imported</Badge>;
    case 'error':
      return <Badge className="bg-red-600 text-white">Error</Badge>;
    default:
      return <Badge>{status}</Badge>;
  }
}

const ImportWizard = () => {
  const { accountId: routeAccountId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const bankAccountId = routeAccountId || searchParams.get('account_id') || '';

  const [step, setStep] = useState(1);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(bankAccountId || '');
  const [loadingAccounts, setLoadingAccounts] = useState(true);

  const [file, setFile] = useState(null);
  const [parseResult, setParseResult] = useState(null); // { encoding, delimiter, headers, sampleRows, suggestedMapping }
  const [mappings, setMappings] = useState([]); // saved presets
  const [selectedMappingId, setSelectedMappingId] = useState('');

  const [mappingForm, setMappingForm] = useState({
    name: '',
    encoding: 'utf-8',
    delimiter: '',
    header_row_index: 0,
    date_format: 'DD/MM/YYYY',
    sign_convention: 'separate_debit_credit',
    column_map: {},
    save_as_preset: false,
  });

  const [previewResult, setPreviewResult] = useState(null);
  const [commitResult, setCommitResult] = useState(null);

  const [statementDates, setStatementDates] = useState({
    start: '',
    end: '',
    opening: '',
    closing: '',
    notes: '',
    allow_duplicate_file: false,
  });

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // ---------------- Load bank accounts + mappings ----------------

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingAccounts(true);
        const data = await bankingAPI.getAccounts({ include_inactive: false });
        if (cancelled) return;
        const reconcilable = (data || []).filter((a) => a.is_reconcilable !== false);
        setAccounts(reconcilable);
        if (!selectedAccountId && reconcilable.length > 0) {
          setSelectedAccountId(reconcilable[0].id);
        }
      } catch (err) {
        setError(err?.response?.data?.error || err.message || 'Failed to load bank accounts');
      } finally {
        if (!cancelled) setLoadingAccounts(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedAccountId) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await reconciliationAPI.listMappings({ bank_account_id: selectedAccountId });
        if (cancelled) return;
        setMappings(list || []);
        const def = (list || []).find((m) => m.is_default);
        if (def && !selectedMappingId) {
          applyMappingPreset(def);
        }
      } catch (_) { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountId]);

  const applyMappingPreset = (m) => {
    if (!m) return;
    setSelectedMappingId(m.id);
    setMappingForm((prev) => ({
      ...prev,
      name: m.name,
      encoding: m.encoding || 'utf-8',
      delimiter: m.delimiter || '',
      header_row_index: m.header_row_index || 0,
      date_format: m.date_format || 'DD/MM/YYYY',
      sign_convention: m.sign_convention || 'separate_debit_credit',
      column_map: { ...(m.column_map || {}) },
    }));
  };

  // ---------------- Step 1: Upload + auto parse ----------------

  const handleFileChange = async (e) => {
    setError('');
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setParseResult(null);
    setPreviewResult(null);
    setCommitResult(null);
    try {
      setBusy(true);
      const res = await reconciliationAPI.parseFile(f, {
        encoding: mappingForm.encoding || undefined,
        delimiter: mappingForm.delimiter || undefined,
        header_row_index: mappingForm.header_row_index || undefined,
      });
      setParseResult(res);
      setMappingForm((prev) => ({
        ...prev,
        encoding: res.encoding || prev.encoding,
        delimiter: res.delimiter || prev.delimiter,
        header_row_index: res.headerRowIndex ?? prev.header_row_index,
        column_map: {
          ...(res.suggestedMapping || {}),
          ...(prev.column_map || {}), // preset overrides suggestion if already present
        },
      }));
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Could not parse file');
    } finally {
      setBusy(false);
    }
  };

  // Re-parse when the user changes encoding / delimiter / header row.
  const reparse = useCallback(async () => {
    if (!file) return;
    try {
      setBusy(true);
      const res = await reconciliationAPI.parseFile(file, {
        encoding: mappingForm.encoding || undefined,
        delimiter: mappingForm.delimiter || undefined,
        header_row_index: mappingForm.header_row_index || undefined,
      });
      setParseResult(res);
      setMappingForm((prev) => ({
        ...prev,
        encoding: res.encoding || prev.encoding,
        delimiter: res.delimiter || prev.delimiter,
      }));
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Could not re-parse file');
    } finally {
      setBusy(false);
    }
  }, [file, mappingForm.encoding, mappingForm.delimiter, mappingForm.header_row_index]);

  // ---------------- Step 3: Preview against DB ----------------

  const runPreview = async () => {
    setError('');
    if (!file || !selectedAccountId) {
      setError('Pick a bank account and file first.');
      return;
    }
    try {
      setBusy(true);
      const body = {
        bank_account_id: selectedAccountId,
        mapping_id: selectedMappingId || undefined,
        encoding: mappingForm.encoding,
        delimiter: mappingForm.delimiter,
        header_row_index: mappingForm.header_row_index,
        date_format: mappingForm.date_format,
        sign_convention: mappingForm.sign_convention,
        column_map: mappingForm.column_map,
      };
      const res = await reconciliationAPI.previewImport(file, body);
      setPreviewResult(res);
      if (res.dateRange?.start && !statementDates.start) {
        setStatementDates((prev) => ({
          ...prev,
          start: res.dateRange.start,
          end: res.dateRange.end || '',
        }));
      }
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Preview failed');
    } finally {
      setBusy(false);
    }
  };

  // ---------------- Step 4: Commit ----------------

  const runCommit = async () => {
    setError('');
    if (!file || !selectedAccountId || !previewResult) {
      setError('Run a preview before committing.');
      return;
    }
    try {
      setBusy(true);

      // Optionally persist the mapping as a preset BEFORE committing so the
      // statement_imports row can reference it via mapping_id.
      let effectiveMappingId = selectedMappingId || '';
      if (mappingForm.save_as_preset && mappingForm.name) {
        try {
          const created = await reconciliationAPI.createMapping({
            name: mappingForm.name,
            bank_account_id: selectedAccountId,
            delimiter: mappingForm.delimiter || ',',
            encoding: mappingForm.encoding || 'utf-8',
            header_row_index: mappingForm.header_row_index || 0,
            date_format: mappingForm.date_format,
            sign_convention: mappingForm.sign_convention,
            column_map: mappingForm.column_map,
            is_default: false,
          });
          effectiveMappingId = created.id;
          setSelectedMappingId(created.id);
          setMappings((prev) => [...prev, created]);
        } catch (err) {
          // Non-fatal: the import can still proceed without a preset.
          console.warn('Could not save preset:', err?.response?.data?.error || err.message);
        }
      }

      const body = {
        bank_account_id: selectedAccountId,
        mapping_id: effectiveMappingId || undefined,
        encoding: mappingForm.encoding,
        delimiter: mappingForm.delimiter,
        header_row_index: mappingForm.header_row_index,
        date_format: mappingForm.date_format,
        sign_convention: mappingForm.sign_convention,
        column_map: mappingForm.column_map,
        statement_start_date: statementDates.start || undefined,
        statement_end_date: statementDates.end || undefined,
        opening_balance_reported: statementDates.opening || undefined,
        closing_balance_reported: statementDates.closing || undefined,
        notes: statementDates.notes || undefined,
        allow_duplicate_file: statementDates.allow_duplicate_file ? 'true' : undefined,
      };
      const res = await reconciliationAPI.commitImport(file, body);
      // Phase 2/3: attempt auto-pairing of any pending two-sided events
      // now that the counterpart side may have landed. This covers both
      // pending_other_side internal transfers and pending_*_side credit
      // card settlements. Best-effort; failures do not block the import.
      let autoPair = null;
      let autoPairSettlements = null;
      let autoMatch = null;
      try {
        autoPair = await reconciliationAPI.autoPairTransfers({
          import_id: res?.import?.id,
          bank_account_id: selectedAccountId,
        });
      } catch (_) {
        // ignore -- non-fatal
      }
      try {
        autoPairSettlements = await reconciliationAPI.autoPairCreditCardSettlements({
          import_id: res?.import?.id,
          bank_account_id: selectedAccountId,
        });
      } catch (_) {
        // ignore -- non-fatal
      }
      try {
        autoMatch = await reconciliationAPI.autoMatchBankTransactions({
          import_id: res?.import?.id,
          bank_account_id: selectedAccountId,
        });
      } catch (_) {
        // ignore -- non-fatal
      }
      setCommitResult({ ...res, autoPair, autoPairSettlements, autoMatch });
    } catch (err) {
      const resp = err?.response?.data;
      if (resp?.existingImport) {
        setError(
          `${resp.error || 'This file was already imported.'} You can tick "allow duplicate file" above to override.`
        );
      } else {
        setError(resp?.error || err.message || 'Commit failed');
      }
    } finally {
      setBusy(false);
    }
  };

  // ---------------- Step navigation ----------------

  const canAdvance = useMemo(() => {
    if (step === 1) return !!file && !!selectedAccountId && !!parseResult;
    if (step === 2) {
      const cm = mappingForm.column_map || {};
      const hasDate = !!cm.date;
      const hasAmount = !!cm.amount || (!!cm.debit && !!cm.credit) || !!cm.debit || !!cm.credit;
      return hasDate && hasAmount;
    }
    if (step === 3) return !!previewResult;
    return true;
  }, [step, file, selectedAccountId, parseResult, mappingForm.column_map, previewResult]);

  const goNext = async () => {
    if (!canAdvance) return;
    if (step === 2 && !previewResult) {
      await runPreview();
    }
    setStep((s) => Math.min(4, s + 1));
  };

  const goBack = () => setStep((s) => Math.max(1, s - 1));

  // ---------------- Render ----------------

  const summary = previewResult?.summary;

  return (
    <Layout>
      <div className="mx-auto max-w-6xl p-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
              Import bank statement
            </h1>
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              Upload a CSV exported from your bank, map columns to SparxMate fields, check the preview, then commit.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/tenant/banking')}>
            Back to banking
          </Button>
        </div>

        {/* Stepper */}
        <div className="mb-6 flex items-center gap-2">
          {STEPS.map((s, idx) => (
            <div key={s.id} className="flex items-center gap-2">
              <div
                className={
                  'flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ' +
                  (s.id < step
                    ? 'bg-green-600 text-white'
                    : s.id === step
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300')
                }
              >
                {s.id < step ? <CheckCircle2 className="h-4 w-4" /> : s.id}
              </div>
              <span className={'text-xs ' + (s.id === step ? 'font-semibold' : '')}>
                {s.label}
              </span>
              {idx < STEPS.length - 1 && <div className="h-px w-10 bg-gray-300 dark:bg-gray-600" />}
            </div>
          ))}
        </div>

        {error && (
          <Card className="mb-4 border-red-400 bg-red-50 dark:bg-red-950/30">
            <CardContent className="flex gap-2 p-4 text-xs text-red-700 dark:text-red-300">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </CardContent>
          </Card>
        )}

        {/* Step 1 */}
        {step === 1 && (
          <Card>
            <CardContent className="p-6 space-y-4">
              <div>
                <FormLabel>Bank / card / loan account</FormLabel>
                {loadingAccounts ? (
                  <div className="text-xs text-gray-500">Loading accounts…</div>
                ) : (
                  <Select
                    value={selectedAccountId}
                    onChange={(e) => setSelectedAccountId(e.target.value)}
                  >
                    <option value="">Select an account…</option>
                    {accounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.account_name} — {a.account_type}
                        {a.ledger_class ? ` (${a.ledger_class})` : ''}
                      </option>
                    ))}
                  </Select>
                )}
              </div>

              <div>
                <FormLabel>Statement file (.csv / .tsv / .txt, up to 10 MB)</FormLabel>
                <label className="mt-1 flex cursor-pointer items-center justify-center gap-2 rounded border border-dashed border-gray-300 px-4 py-8 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800">
                  <Upload className="h-4 w-4" />
                  <span className="text-xs">
                    {file ? file.name : 'Click to choose a file'}
                  </span>
                  <input
                    type="file"
                    accept=".csv,.tsv,.txt"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </label>
                {file && parseResult && (
                  <div className="mt-3 text-xs text-gray-600 dark:text-gray-400">
                    <div><FileSpreadsheet className="inline h-3 w-3" /> Detected encoding: <b>{parseResult.encoding}</b>, delimiter: <b>{JSON.stringify(parseResult.delimiter)}</b>, {parseResult.headers.length} columns.</div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2 */}
        {step === 2 && parseResult && (
          <div className="space-y-4">
            {mappings.length > 0 && (
              <Card>
                <CardContent className="p-4">
                  <FormLabel>Start from a saved preset</FormLabel>
                  <Select
                    value={selectedMappingId}
                    onChange={(e) => {
                      const m = mappings.find((x) => x.id === e.target.value);
                      if (m) applyMappingPreset(m);
                      else setSelectedMappingId('');
                    }}
                  >
                    <option value="">— No preset / custom —</option>
                    {mappings.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}{m.is_default ? ' (default)' : ''}
                      </option>
                    ))}
                  </Select>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardContent className="p-4 grid grid-cols-1 gap-4 md:grid-cols-4">
                <div>
                  <FormLabel>Encoding</FormLabel>
                  <Select
                    value={mappingForm.encoding}
                    onChange={(e) => setMappingForm((p) => ({ ...p, encoding: e.target.value }))}
                    onBlur={reparse}
                  >
                    {ENCODINGS.map((e) => (
                      <option key={e.value} value={e.value}>{e.label}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <FormLabel>Delimiter</FormLabel>
                  <Select
                    value={mappingForm.delimiter}
                    onChange={(e) => setMappingForm((p) => ({ ...p, delimiter: e.target.value }))}
                    onBlur={reparse}
                  >
                    {DELIMITERS.map((d) => (
                      <option key={d.label} value={d.value}>{d.label}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <FormLabel>Header row index (0-based)</FormLabel>
                  <Input
                    type="number"
                    min={0}
                    value={mappingForm.header_row_index}
                    onChange={(e) => setMappingForm((p) => ({ ...p, header_row_index: parseInt(e.target.value, 10) || 0 }))}
                    onBlur={reparse}
                  />
                </div>
                <div>
                  <FormLabel>Date format</FormLabel>
                  <Select
                    value={mappingForm.date_format}
                    onChange={(e) => setMappingForm((p) => ({ ...p, date_format: e.target.value }))}
                  >
                    {DATE_FORMATS.map((f) => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </Select>
                </div>
                <div className="md:col-span-4">
                  <FormLabel>Sign convention</FormLabel>
                  <Select
                    value={mappingForm.sign_convention}
                    onChange={(e) => setMappingForm((p) => ({ ...p, sign_convention: e.target.value }))}
                  >
                    {SIGN_CONVENTIONS.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </Select>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="mb-2 text-xs font-semibold">Column mapping</div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {MAPPING_FIELDS.map((f) => (
                    <div key={f.key} className="flex items-center gap-2">
                      <FormLabel className="w-48">
                        {f.label}{f.required && <span className="ml-1 text-red-500">*</span>}
                      </FormLabel>
                      <Select
                        value={mappingForm.column_map?.[f.key] || ''}
                        onChange={(e) => setMappingForm((p) => ({
                          ...p,
                          column_map: { ...p.column_map, [f.key]: e.target.value || undefined },
                        }))}
                      >
                        <option value="">— Not mapped —</option>
                        {parseResult.headers.map((h) => (
                          <option key={h} value={h}>{h}</option>
                        ))}
                      </Select>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="mb-2 text-xs font-semibold">Preview (first {parseResult.rows?.length || 0} rows)</div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow isHeader>
                        {parseResult.headers.map((h) => (
                          <TableHead key={h}>{h}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(parseResult.rows || []).map((row, i) => (
                        <TableRow key={i}>
                          {parseResult.headers.map((h) => (
                            <TableCell key={h}>{String(row[h] ?? '')}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <input
                    id="save-preset"
                    type="checkbox"
                    checked={mappingForm.save_as_preset}
                    onChange={(e) => setMappingForm((p) => ({ ...p, save_as_preset: e.target.checked }))}
                  />
                  <label htmlFor="save-preset" className="text-xs">
                    <Save className="mr-1 inline h-3 w-3" />
                    Save this mapping as a preset for next time
                  </label>
                </div>
                {mappingForm.save_as_preset && (
                  <Input
                    placeholder="Preset name, e.g. “Barclays Business CSV”"
                    value={mappingForm.name}
                    onChange={(e) => setMappingForm((p) => ({ ...p, name: e.target.value }))}
                  />
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 3 */}
        {step === 3 && (
          <div className="space-y-4">
            {!previewResult ? (
              <Card>
                <CardContent className="p-6 text-center">
                  <Button onClick={runPreview} disabled={busy}>
                    {busy ? 'Validating…' : 'Run validation'}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                {previewResult.existingImport && (
                  <Card className="border-amber-400 bg-amber-50 dark:bg-amber-950/30">
                    <CardContent className="p-4 text-xs">
                      <AlertTriangle className="mr-1 inline h-4 w-4 text-amber-600" />
                      This file was already imported on{' '}
                      <b>{new Date(previewResult.existingImport.importedAt).toISOString().slice(0, 10)}</b>{' '}
                      as <code>{previewResult.existingImport.originalFilename}</code>. Tick "allow duplicate file" on the next step to override.
                    </CardContent>
                  </Card>
                )}

                <Card>
                  <CardContent className="p-4 grid grid-cols-2 gap-3 md:grid-cols-5">
                    <KV label="Total rows" value={summary?.total} />
                    <KV label="New" value={summary?.newRows} good />
                    <KV label="Duplicate in file" value={summary?.duplicateInFile} warn />
                    <KV label="Already imported" value={summary?.duplicateExisting} warn />
                    <KV label="Errors" value={summary?.errorRows} danger />
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4 text-xs text-gray-600 dark:text-gray-400">
                    Date range: {previewResult.dateRange?.start || '—'} → {previewResult.dateRange?.end || '—'}
                    {' • '}
                    Ledger class: {previewResult.ledgerClass}
                    {' • '}
                    Sign convention: {previewResult.signConvention}
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow isHeader>
                          <TableHead>#</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Reference</TableHead>
                          <TableHead align="right">Amount</TableHead>
                          <TableHead align="right">Balance</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(previewResult.lines || []).slice(0, 200).map((l) => (
                          <TableRow key={l.lineNumber}>
                            <TableCell>{l.lineNumber}</TableCell>
                            <TableCell>{l.transactionDate || '—'}</TableCell>
                            <TableCell>{l.description || '—'}</TableCell>
                            <TableCell>{l.reference || '—'}</TableCell>
                            <TableCell align="right">{formatMoney(l.amount)}</TableCell>
                            <TableCell align="right">{l.runningBalance != null ? formatMoney(l.runningBalance) : '—'}</TableCell>
                            <TableCell>{statusBadge(l.status)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {previewResult.lines?.length > 200 && (
                      <div className="p-3 text-center text-xs text-gray-500">
                        Showing first 200 of {previewResult.lines.length} rows.
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        )}

        {/* Step 4 */}
        {step === 4 && (
          <div className="space-y-4">
            {!commitResult ? (
              <>
                <Card>
                  <CardContent className="p-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div>
                      <FormLabel>Statement start date</FormLabel>
                      <Input
                        type="date"
                        value={statementDates.start}
                        onChange={(e) => setStatementDates((p) => ({ ...p, start: e.target.value }))}
                      />
                    </div>
                    <div>
                      <FormLabel>Statement end date</FormLabel>
                      <Input
                        type="date"
                        value={statementDates.end}
                        onChange={(e) => setStatementDates((p) => ({ ...p, end: e.target.value }))}
                      />
                    </div>
                    <div>
                      <FormLabel>Opening balance on statement (optional)</FormLabel>
                      <Input
                        type="number"
                        step="0.01"
                        value={statementDates.opening}
                        onChange={(e) => setStatementDates((p) => ({ ...p, opening: e.target.value }))}
                      />
                    </div>
                    <div>
                      <FormLabel>Closing balance on statement (optional)</FormLabel>
                      <Input
                        type="number"
                        step="0.01"
                        value={statementDates.closing}
                        onChange={(e) => setStatementDates((p) => ({ ...p, closing: e.target.value }))}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <FormLabel>Notes (optional)</FormLabel>
                      <Input
                        value={statementDates.notes}
                        onChange={(e) => setStatementDates((p) => ({ ...p, notes: e.target.value }))}
                      />
                    </div>
                    {previewResult?.existingImport && (
                      <div className="md:col-span-2 flex items-center gap-2">
                        <input
                          id="allow-dup"
                          type="checkbox"
                          checked={statementDates.allow_duplicate_file}
                          onChange={(e) => setStatementDates((p) => ({ ...p, allow_duplicate_file: e.target.checked }))}
                        />
                        <label htmlFor="allow-dup" className="text-xs">
                          Allow duplicate file (overrides file-hash block — tenant admin only)
                        </label>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="text-xs">
                      Ready to commit {summary?.newRows || 0} new line{summary?.newRows === 1 ? '' : 's'}.
                      {(summary?.duplicateInFile || 0) + (summary?.duplicateExisting || 0) > 0 && (
                        <> {summary.duplicateInFile + summary.duplicateExisting} duplicate{' '}
                          line{summary.duplicateInFile + summary.duplicateExisting === 1 ? '' : 's'} will be skipped.</>
                      )}
                      {summary?.errorRows > 0 && (
                        <> {summary.errorRows} error row{summary.errorRows === 1 ? '' : 's'} will be skipped.</>
                      )}
                    </div>
                    <Button onClick={runCommit} disabled={busy || !previewResult}>
                      {busy ? 'Committing…' : 'Commit import'}
                    </Button>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="border-green-500 bg-green-50 dark:bg-green-950/30">
                <CardContent className="p-6 space-y-3">
                  <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                    <CheckCircle2 className="h-5 w-5" />
                    <div className="text-sm font-semibold">Import committed</div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-5 text-xs">
                    <KV label="Total" value={commitResult.summary?.total} />
                    <KV label="Imported" value={commitResult.summary?.imported} good />
                    <KV label="Dup. in file" value={commitResult.summary?.duplicateInFile} warn />
                    <KV label="Already imported" value={commitResult.summary?.duplicateExisting} warn />
                    <KV label="Errors" value={commitResult.summary?.errorRows} danger />
                  </div>
                  {commitResult.autoPair && (commitResult.autoPair.paired > 0 || (commitResult.autoPair.skipped?.length || 0) > 0) && (
                    <div className="text-xs text-gray-600 dark:text-gray-300">
                      Auto-paired <span className="font-semibold">{commitResult.autoPair.paired}</span> pending
                      transfer{commitResult.autoPair.paired === 1 ? '' : 's'} with this import.
                      {(commitResult.autoPair.skipped?.length || 0) > 0 && (
                        <> {commitResult.autoPair.skipped.length} left for manual review.</>
                      )}
                    </div>
                  )}
                  {commitResult.autoMatch && (commitResult.autoMatch.matched > 0 || (commitResult.autoMatch.skipped?.length || 0) > 0) && (
                    <div className="text-xs text-gray-600 dark:text-gray-300">
                      Auto-matched <span className="font-semibold">{commitResult.autoMatch.matched}</span> statement
                      line{commitResult.autoMatch.matched === 1 ? '' : 's'} to ledger transactions (draft).
                      {(commitResult.autoMatch.skipped?.length || 0) > 0 && (
                        <> {commitResult.autoMatch.skipped.length} skipped.</>
                      )}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setStep(1);
                        setFile(null);
                        setParseResult(null);
                        setPreviewResult(null);
                        setCommitResult(null);
                      }}
                    >
                      Import another file
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => navigate(`/tenant/banking/accounts/${selectedAccountId}/transactions`)}
                    >
                      View account transactions
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Navigation */}
        <div className="mt-6 flex items-center justify-between">
          <Button variant="outline" onClick={goBack} disabled={step === 1 || busy}>
            <ChevronLeft className="mr-1 h-4 w-4" /> Back
          </Button>
          {step < 4 && !commitResult && (
            <Button onClick={goNext} disabled={!canAdvance || busy}>
              {step === 2 ? 'Validate' : 'Next'} <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </Layout>
  );
};

const KV = ({ label, value, good, warn, danger }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
    <div
      className={
        'text-base font-semibold ' +
        (danger && value
          ? 'text-red-600'
          : warn && value
            ? 'text-amber-600'
            : good && value
              ? 'text-green-600'
              : 'text-gray-900 dark:text-white')
      }
    >
      {value ?? 0}
    </div>
  </div>
);

export default ImportWizard;
